import { INVITED_BY_QUERY_PARAM, parseInvitedByUserId } from "./party-join-attribution";

export const JOIN_AFTER_AUTH_PARAM = "join";
export const JOIN_AFTER_AUTH_VALUE = "1";

export type AutoJoinAfterAuthDecision = "idle" | "auto_join" | "enter_party";

export function hasJoinAfterAuthIntent(
  value: string | string[] | undefined | null,
): boolean {
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) {
    return false;
  }

  const normalized = raw.trim().toLowerCase();
  return (
    normalized === JOIN_AFTER_AUTH_VALUE ||
    normalized === "true"
  );
}

export function decideAutoJoinAfterAuth(input: {
  hasJoinIntent: boolean;
  isSignedIn: boolean;
  isAlreadyMember: boolean;
  hasEmailMismatch: boolean;
}): AutoJoinAfterAuthDecision {
  if (!input.hasJoinIntent || !input.isSignedIn || input.hasEmailMismatch) {
    return "idle";
  }

  if (input.isAlreadyMember) {
    return "enter_party";
  }

  return "auto_join";
}

function splitPathAndSearch(path: string): { pathname: string; search: string } {
  const queryIndex = path.indexOf("?");
  if (queryIndex === -1) {
    return { pathname: path, search: "" };
  }

  return {
    pathname: path.slice(0, queryIndex),
    search: path.slice(queryIndex + 1),
  };
}

export function withJoinAfterAuthIntent(path: string): string {
  const trimmed = path.trim();
  const isSafeInAppPath =
    trimmed.startsWith("/") && !trimmed.startsWith("//");
  const safePath = isSafeInAppPath ? trimmed : "/";
  const { pathname, search } = splitPathAndSearch(safePath);
  const params = new URLSearchParams(search);
  params.set(JOIN_AFTER_AUTH_PARAM, JOIN_AFTER_AUTH_VALUE);
  return `${pathname}?${params.toString()}`;
}

export function withoutJoinAfterAuthIntent(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) {
    return trimmed;
  }

  const { pathname, search } = splitPathAndSearch(trimmed);
  const params = new URLSearchParams(search);
  params.delete(JOIN_AFTER_AUTH_PARAM);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function buildJoinInvitePath(
  inviteCode: string,
  invitedEmail?: string | null,
  invitedByUserId?: string | null,
): string {
  const base = `/join/${inviteCode}`;
  const params = new URLSearchParams();
  const email = invitedEmail?.trim();
  if (email) {
    params.set("invited_email", email);
  }
  const invitedBy = parseInvitedByUserId(invitedByUserId);
  if (invitedBy) {
    params.set(INVITED_BY_QUERY_PARAM, invitedBy);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

const autoJoinInFlight = new Map<string, Promise<unknown>>();

export function shareAutoJoinInFlight<T>(
  inviteCode: string,
  start: () => Promise<T>,
): Promise<T> {
  const existing = autoJoinInFlight.get(inviteCode);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = Promise.resolve()
    .then(start)
    .finally(() => {
      autoJoinInFlight.delete(inviteCode);
    });
  autoJoinInFlight.set(inviteCode, promise);
  return promise;
}

export function resetAutoJoinInFlightForTests(): void {
  autoJoinInFlight.clear();
}
